import type { ColumnBarFill } from "./tableMappingBuilder";
import { getTableCellRaw } from "./tablePayload";

export type ColumnNumericRange = {
  min: number;
  max: number;
};

export function toNumericCellValue(value: unknown): number | null {
  if (value == null || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const parsed = Number(String(value).replace(/,/g, "").trim());
  return Number.isFinite(parsed) ? parsed : null;
}

export function computeColumnNumericRange(
  rows: Record<string, unknown>[],
  field: string,
  header: string,
  override?: Pick<ColumnBarFill, "min" | "max">
): ColumnNumericRange | null {
  if (override?.min != null && override?.max != null) {
    return { min: override.min, max: override.max };
  }
  let min = override?.min ?? Number.POSITIVE_INFINITY;
  let max = override?.max ?? Number.NEGATIVE_INFINITY;
  let found = false;
  for (const row of rows) {
    const num = toNumericCellValue(getTableCellRaw(row, field, header));
    if (num == null) continue;
    found = true;
    if (override?.min == null && num < min) min = num;
    if (override?.max == null && num > max) max = num;
  }
  if (!found) {
    if (override?.min != null || override?.max != null) {
      return {
        min: override.min ?? 0,
        max: override.max ?? 0,
      };
    }
    return null;
  }
  if (!Number.isFinite(min)) min = 0;
  if (!Number.isFinite(max)) max = 0;
  return { min, max };
}

export type BarFillLayout = {
  positivePct: number;
  negativePct: number;
};

export function computeBarFillLayout(
  value: unknown,
  barFill: ColumnBarFill,
  range: ColumnNumericRange | null
): BarFillLayout {
  const num = toNumericCellValue(value);
  if (num == null || !range) {
    return { positivePct: 0, negativePct: 0 };
  }
  const mode = barFill.mode ?? "positive";
  let positivePct = 0;
  let negativePct = 0;

  if ((mode === "positive" || mode === "both") && num > 0 && range.max > 0) {
    positivePct = Math.min(100, Math.max(0, (num / range.max) * 100));
  }
  if ((mode === "negative" || mode === "both") && num < 0 && range.min < 0) {
    negativePct = Math.min(100, Math.max(0, (num / range.min) * 100));
  }

  return { positivePct, negativePct };
}
